import React from "react";
import {
  ProfileContext,
  defaultProfile,
  Profile
} from "@velaro/velaro-component-lib";
import useApi from "../hooks/useApi";
import authService from "../services/AuthService";
import InitStuckBanner from "../components/InitStuckBanner";
import { useInitStuckDetector } from "../hooks/useInitStuckDetector";

// Seed from the token-exchange profile so the name/avatar shows immediately
// even before Profile/Me returns. The full fetch still runs to get teamIds,
// roles, status settings, etc.
function seedProfile(): Profile {
  const s = authService.profile as any;
  if (!s) return defaultProfile;
  const first = s.firstName || "";
  const last = s.lastName || "";
  const display =
    first && last
      ? `${first} ${last}`
      : first || s.displayName || s.email || s.userName || "";
  if (!first && !display) return defaultProfile;
  return {
    ...defaultProfile,
    picture: s.picture || "",
    firstName: first,
    lastName: last,
    displayName: display
  };
}

// Redeploy slot-swap worker recycles can make the messaging API blip for up to
// ~2-3 minutes (see deploy-staging.yml "Prevents false-positive pages during
// slot swap / app restart warmup" comment) even when the swap itself is healthy.
// Retry through that whole window before ever surfacing the "reload" banner —
// children already render off the seeded profile in the meantime, so a blip is
// invisible to the user unless the backend is genuinely down.
const RETRY_DELAYS_MS = [
  3000, 5000, 8000, 8000, 10000, 10000, 15000, 15000, 20000, 20000, 20000
];
const STUCK_THRESHOLD_MS =
  RETRY_DELAYS_MS.reduce((sum, d) => sum + d, 0) + 15000;

export default function ProfileProvider(props: { children: React.ReactNode }) {
  const [loading, setLoading] = React.useState(true);
  const [profile, setProfile] = React.useState<Profile>(seedProfile);
  const [fetchError, setFetchError] = React.useState(false);
  const api = useApi();
  const stuck = useInitStuckDetector(loading, STUCK_THRESHOLD_MS);

  React.useEffect(() => {
    async function load(attempt = 0) {
      try {
        const response = await api.messaging.get("Profile/Me");
        if (response.ok) {
          setProfile(await response.json());
          setLoading(false);
          return;
        }
        console.error(
          `[ProfileProvider] Profile/Me returned ${response.status} — JWT mismatch or messaging API error. Name/avatar will use token-exchange data only.`
        );
        if (response.status === 401) {
          setLoading(false);
          return;
        }
      } catch (err) {
        console.error("[ProfileProvider] Profile/Me fetch failed:", err);
      }

      if (attempt < RETRY_DELAYS_MS.length) {
        setTimeout(() => load(attempt + 1), RETRY_DELAYS_MS[attempt]);
      } else {
        setFetchError(true);
        setLoading(false);
      }
    }

    load();
  }, [api]);

  const groupMap = React.useMemo(() => {
    const map: { [groupId: number]: boolean } = {};

    if (!profile.teamIds) {
      return map;
    }

    profile.teamIds.forEach((groupId) => {
      map[groupId] = true;
    });

    return map;
  }, [profile.teamIds]);

  const belongsToGroup = React.useCallback(
    (groupId: number) => {
      // null/undefined group id means all users can see.

      if (!groupId) {
        return true;
      }

      return groupMap[groupId] === true;
    },
    [groupMap]
  );

  if (stuck && loading) {
    return (
      <InitStuckBanner message="Unable to load your profile. Please reload or sign out." />
    );
  }

  if (fetchError && !loading) {
    return (
      <InitStuckBanner message="Unable to load your profile. Please reload or sign out." />
    );
  }

  return (
    <ProfileContext.Provider
      value={{ profile, loading, setProfile, belongsToGroup }}
    >
      {props.children}
    </ProfileContext.Provider>
  );
}
